import { useState, useRef, useEffect, useCallback } from 'react';
import { Pencil } from 'lucide-react';

interface InlineEditableTitleProps {
  value: string;
  onSave: (newValue: string) => Promise<void> | void;
  className?: string;
  maxLength?: number;
  placeholder?: string;
}

export function InlineEditableTitle({
  value,
  onSave,
  className = '',
  maxLength = 100,
  placeholder = 'Untitled Project',
}: InlineEditableTitleProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isEditing) {
      setEditValue(value);
    }
  }, [value, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = useCallback(async () => {
    const trimmed = editValue.trim();
    if (!trimmed) {
      setEditValue(value);
      setIsEditing(false);
      return;
    }
    if (trimmed === value) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    try {
      await onSave(trimmed);
      setIsEditing(false);
    } catch {
      setEditValue(value);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  }, [editValue, value, onSave]);

  const handleCancel = useCallback(() => {
    setEditValue(value);
    setIsEditing(false);
  }, [value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
      e.stopPropagation();
    },
    [handleSave, handleCancel]
  );

  if (isEditing) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value.slice(0, maxLength))}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          disabled={isSaving}
          className={`bg-white/10 border border-white/20 rounded px-2 py-0.5 text-sm text-zinc-200
            outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50
            disabled:opacity-50 ${className}`}
          style={{
            minWidth: '120px',
            maxWidth: '400px',
            width: `${Math.max(editValue.length * 7.5 + 24, 120)}px`,
          }}
          placeholder={placeholder}
        />
        {isSaving && (
          <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        )}
      </div>
    );
  }

  return (
    <button
      onClick={() => setIsEditing(true)}
      className={`group flex items-center gap-1.5 px-1.5 py-0.5 -ml-1.5 rounded
        hover:bg-white/[0.06] transition-colors cursor-text ${className}`}
      title="Click to rename"
    >
      <span className="text-sm font-medium text-zinc-200 truncate max-w-[240px]">
        {value || placeholder}
      </span>
      <Pencil className="w-3 h-3 text-white/0 group-hover:text-white/40 transition-colors flex-shrink-0" />
    </button>
  );
}
